import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Grid, Typography, Box, FormLabel } from '@mui/material';
import {
  useAdditionalQuoteDocumentationTableInstance,
  AdditionalQuoteDocumentationTable,
} from 'quoting/quote-review/tables';
import { AdditionalQuoteDocumentationItem, QuoteDoc, QuoteDocument } from 'shared/types';
import { QuoteFormFileUploadField } from 'shared/controls';
import { useUserPermissions } from 'shared/hooks';
import { PERMISSIONS } from 'shared/constants';

interface Props {
  quote?: QuoteDocument;
}

export const QuoteReviewDocumentation = ({ quote }: Props) => {
  const { watch } = useFormContext();
  const currentQuoteFilesToDelete = watch('quoteFilesToDelete') as QuoteDoc[];

  const userPermissions = useUserPermissions();
  const hasFileAccessPermission: boolean =
    userPermissions.includes(PERMISSIONS['write']) || userPermissions.includes(PERMISSIONS['global.write']);

  const currentQuoteFilesToDeleteLength = currentQuoteFilesToDelete?.length;
  const tableData: AdditionalQuoteDocumentationItem[] = useMemo(() => {
    const quoteIdsMarkedForDeletion = !!currentQuoteFilesToDelete ? currentQuoteFilesToDelete.map(q => q.id) : [];
    const quoteFilesNotMarkedForDeletion = !!quote?.quoteDocumentation
      ? !!currentQuoteFilesToDeleteLength
        ? [...quote.quoteDocumentation.filter(q => !quoteIdsMarkedForDeletion.includes(q.id))]
        : [...quote?.quoteDocumentation]
      : [];
    return quoteFilesNotMarkedForDeletion;
  }, [quote?.quoteDocumentation, currentQuoteFilesToDelete, currentQuoteFilesToDeleteLength]);
  const currentStatus = useWatch<{ status: string }>({ name: 'status' });
  const additionalQuoteDocumentationTableInstance = useAdditionalQuoteDocumentationTableInstance(tableData);
  const currentQuoteHasQuoteDocument = quote?.quoteDocumentation?.some(q => q.documentType === 'QUOTE');
  const isQuoteFileRequired =
    (currentStatus === 'APPROVED' || currentStatus === 'REVIEWED') && !currentQuoteHasQuoteDocument;

  return (
    <Grid item container>
      <Grid xs={12} item container sx={{ paddingTop: 3 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexFlow: 'row', alignItems: 'center' }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
              Previously Uploaded Documentation
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <AdditionalQuoteDocumentationTable instance={additionalQuoteDocumentationTableInstance} />
        </Grid>
        <Grid xs={6} item container sx={{ paddingTop: 3 }}>
          <Box sx={{ display: 'flex', flexFlow: 'row', alignItems: 'center' }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
              Quote Documentation
            </Typography>
          </Box>
        </Grid>
        <Grid xs={6} item container></Grid>
        <Grid xs={6} item container>
          <Grid container>
            <Grid xs={12} item sx={{ paddingTop: 1 }}>
              <FormLabel>Additional Documentation</FormLabel>
            </Grid>
            <Grid xs={12} item>
              <QuoteFormFileUploadField
                name={'additionalDocuments'}
                isMultiple={true}
                accept={'application/pdf'}
                disabled={!hasFileAccessPermission}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={6} item container>
          <Grid xs={12} item sx={{ paddingTop: 1 }}>
            <FormLabel>Quote Documents</FormLabel>
          </Grid>
          <Grid xs={12} item>
            <QuoteFormFileUploadField
              name={'quoteFiles'}
              isMultiple={true}
              accept={'application/pdf'}
              disabled={!hasFileAccessPermission}
              rules={{
                required: {
                  message: 'A Quote File is required when approving a quote.',
                  value: isQuoteFileRequired,
                },
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
